import React, { useEffect, useState } from "react";

import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader } from "../ui/Card";
import { Input } from "../ui/Input";

const StockMovementModal = ({
  isOpen,
  onClose,
  item,
  collaborators = [],
  tickets = [],
  defaultType = "entrada",
  onSubmit,
}) => {
  const [form, setForm] = useState({
    type: defaultType,
    collaborator_id: "",
    ticket_id: "",
    quantity: "",
    movement_reason: "",
    reference: "",
    invoice_number: "",
    unit_cost: "",
    description: "",
  });

  useEffect(() => {
    setForm({
      type: defaultType,
      collaborator_id: "",
      ticket_id: "",
      quantity: "",
      movement_reason: "",
      reference: "",
      invoice_number: "",
      unit_cost: "",
      description: "",
    });
  }, [defaultType, item, isOpen]);

  if (!isOpen || !item) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (form.quantity === "") {
      alert("Informe a quantidade da movimentação.");
      return;
    }

    onSubmit?.({
      item_id: item.id,
      type: form.type,
      collaborator_id: form.collaborator_id ? Number(form.collaborator_id) : null,
      ticket_id: form.ticket_id ? Number(form.ticket_id) : null,
      quantity: Number(form.quantity),
      movement_reason: form.movement_reason || null,
      reference: form.reference || null,
      invoice_number: form.invoice_number || null,
      unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
      description: form.description || null,
    });
  };

  const helperText =
    form.type === "ajuste"
      ? "Para ajuste, use valor positivo para aumentar e negativo para reduzir o saldo."
      : `Saldo atual: ${item.quantity} ${item.unit || "Un"}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Movimentar Estoque</h2>
                <p className="text-sm text-gray-500">{item.name}</p>
              </div>
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm text-gray-600">Tipo de Movimentação</label>
                <select
                  value={form.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                  <option value="ajuste">Ajuste</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Quantidade</label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.quantity}
                  onChange={(e) => handleChange("quantity", e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">{helperText}</p>
              </div>

              <div>
                <label className="text-sm text-gray-600">Motivo</label>
                <Input
                  value={form.movement_reason}
                  onChange={(e) => handleChange("movement_reason", e.target.value)}
                  placeholder="Ex: Consumo interno, compra, inventário"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Referência</label>
                <Input
                  value={form.reference}
                  onChange={(e) => handleChange("reference", e.target.value)}
                  placeholder="Ex: NF 123, OS 456, Pedido 789"
                />
              </div>

              {form.type === "saida" && (
                <>
                  <div>
                    <label className="text-sm text-gray-600">Colaborador</label>
                    <select
                      value={form.collaborator_id}
                      onChange={(e) => handleChange("collaborator_id", e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="">Sem colaborador</option>
                      {collaborators.map((collaborator) => (
                        <option key={collaborator.id} value={collaborator.id}>
                          {`${collaborator.first_name || ""} ${collaborator.last_name || ""}`.trim()}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-sm text-gray-600">Chamado</label>
                    <select
                      value={form.ticket_id}
                      onChange={(e) => handleChange("ticket_id", e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="">Sem chamado</option>
                      {tickets.map((ticket) => (
                        <option key={ticket.id} value={ticket.id}>
                          #{ticket.id} - {ticket.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {form.type === "entrada" && (
                <div>
                  <label className="text-sm text-gray-600">NF de Entrada</label>
                  <Input
                    value={form.invoice_number}
                    onChange={(e) => handleChange("invoice_number", e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
              )}

              <div>
                <label className="text-sm text-gray-600">Custo Unitário</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.unit_cost}
                  onChange={(e) => handleChange("unit_cost", e.target.value)}
                  placeholder="Opcional"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Observação</label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Detalhe livre da movimentação"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>Salvar Movimentação</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StockMovementModal;
